/**
 * Rich-text helpers for the inbox email composer (§9).
 *
 * The inbox email reply is authored in a rich-text editor (CKEditor) and sent as
 * HTML — the backend send path is already HTML-aware (`assembleEmailBody` detects
 * markup and `unipile.sendEmail` accepts an HTML body). These helpers bridge the
 * two body shapes the inbox deals with: rich HTML (email) and plain text (the AI
 * draft, canned templates, and the LinkedIn/WhatsApp channels, which are
 * plain-text only). `sanitizeOutboundHtml` hardens the recruiter-authored HTML
 * before it's rendered back into the timeline.
 */

/** A body is treated as HTML when it carries any tag — mirrors the server's
 *  `looksLikeHtml` (message-render.service.js) so both ends agree on the shape. */
export function looksLikeHtml(body: string | null | undefined): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(String(body || ''));
}

function escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Plain text → simple paragraph HTML for seeding the editor (e.g. an AI draft
 *  or a canned template, both authored as plain text). Blank lines split
 *  paragraphs; single newlines become `<br>`. */
export function textToHtml(text: string | null | undefined): string {
    const paragraphs = String(text || '')
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean);
    if (!paragraphs.length) return '';
    return paragraphs.map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`).join('');
}

/** HTML → plain text, preserving line breaks. Used when an email draft is carried
 *  over to a plain-text channel (LinkedIn/WhatsApp) so the recruiter never sees
 *  raw tags in the textarea. */
export function htmlToText(html: string | null | undefined): string {
    const raw = String(html || '');
    if (typeof window === 'undefined' || !window.DOMParser) {
        return raw.replace(/<[^>]+>/g, '').trim();
    }
    let doc: Document;
    try {
        doc = new window.DOMParser().parseFromString(raw, 'text/html');
    } catch {
        return raw.replace(/<[^>]+>/g, '').trim();
    }
    doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    doc.querySelectorAll('p, div, li, blockquote, h1, h2, h3, h4, h5, h6').forEach((el) => {
        el.append('\n');
    });
    return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

/** Does this body carry no real content? Treats an empty editor (`<p></p>`,
 *  `<p>&nbsp;</p>`, stray tags/whitespace) as empty so Send stays disabled. */
export function isHtmlEmpty(html: string | null | undefined): boolean {
    const text = String(html || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, '');
    return text.length === 0;
}

// Elements that must never survive into the rendered timeline — stripped whole
// (content included).
const DANGEROUS_TAGS = [
    'script',
    'style',
    'iframe',
    'object',
    'embed',
    'link',
    'meta',
    'base',
    'form',
    'svg',
    'math',
    'title',
    'noscript',
    'template',
];

// Attributes we keep on any surviving element. Everything else (class, style,
// inline event handlers, data-*) is dropped — the timeline styles content via
// the `.rich-content` container, so element-level styling isn't needed.
const ALLOWED_ATTRS = new Set(['href', 'target', 'rel', 'title']);

const ALLOWED_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function isSafeLink(href: string): boolean {
    try {
        // The browser URL parser removes embedded tabs/newlines and normalizes
        // schemes. Check that canonical protocol, after HTML entities have been
        // decoded by DOMParser. A fixed HTTPS base also permits relative links.
        return ALLOWED_LINK_PROTOCOLS.has(new URL(href, 'https://outreach.invalid/').protocol);
    } catch {
        return false;
    }
}

/**
 * Defensive allowlist sanitizer for the recruiter's own outbound HTML before it's
 * rendered back into the timeline with `dangerouslySetInnerHTML`. The content is
 * first-party (authored in our editor), and inbound provider HTML is never
 * rendered as markup (it shows as cleaned text via the server's `displayBody`),
 * so this is defense-in-depth rather than the only barrier — it removes scripts,
 * event handlers, and `javascript:`/`data:` URLs.
 */
export function sanitizeOutboundHtml(html: string | null | undefined): string {
    const raw = String(html || '');
    if (!raw) return '';
    if (typeof window === 'undefined' || !window.DOMParser) {
        // This result is consumed as HTML, so a missing parser must produce
        // escaped text rather than attempt to sanitize markup with a regex.
        return escapeHtml(raw);
    }
    let doc: Document;
    try {
        doc = new window.DOMParser().parseFromString(raw, 'text/html');
    } catch {
        return escapeHtml(raw);
    }

    doc.querySelectorAll(DANGEROUS_TAGS.join(',')).forEach((el) => el.remove());

    doc.body.querySelectorAll('*').forEach((el) => {
        for (const attr of Array.from(el.attributes)) {
            const name = attr.name.toLowerCase();
            if (name.startsWith('on') || !ALLOWED_ATTRS.has(name)) {
                el.removeAttribute(attr.name);
                continue;
            }
            if (name === 'href' && !isSafeLink(attr.value)) {
                el.removeAttribute(attr.name);
            }
        }
        // Anchors that open a new tab get the standard tab-nabbing guard.
        if (el.tagName === 'A' && el.getAttribute('target')?.toLowerCase() === '_blank') {
            el.setAttribute('rel', 'noopener noreferrer');
        }
    });

    return doc.body.innerHTML;
}
